import { type CustomField } from "@/lib/backend/api";

/**
 * Legacy display contexts that surfaced a custom field to an EMPLOYEE before #10b (Persoonlijke
 * gegevens / onboarding). Only used as a fallback for api responses that predate the explicit
 * `visibleToEmployee` axis.
 */
const LEGACY_EMPLOYEE_VISIBLE_CONTEXTS = ["PROFILE", "ONBOARDING"] as const;

/**
 * Whether an EMPLOYEE may SEE a custom field in the app (#10b "visible to" axis).
 *
 * The api is the authoritative gate — it already excludes non-visible definitions for employee
 * callers and rejects writes to them — but we also filter client-side (defense-in-depth) so a
 * hidden field can never render or be patched from the app.
 *
 * The new model exposes an explicit `visibleToEmployee` flag. When present it is authoritative.
 * When absent (a pre-#10b api response, or a client not yet regenerated) we fall back to the legacy
 * displayContexts rule: not ADMIN_ONLY and carries an employee-facing context. This keeps old and
 * new api versions working.
 */
export function isEmployeeVisibleCustomField(field: CustomField): boolean {
  const def = field.customFieldDefinition;

  if (def.visibleToEmployee !== undefined) {
    return def.visibleToEmployee;
  }

  // Fallback for pre-#10b responses. Typed as string[] so it keeps compiling once
  // generate:api drops ADMIN_ONLY from the displayContexts union.
  const contexts: string[] = def.displayContexts ?? [];
  if (contexts.includes("ADMIN_ONLY")) {
    return false;
  }
  return contexts.some((context) =>
    (LEGACY_EMPLOYEE_VISIBLE_CONTEXTS as readonly string[]).includes(context),
  );
}

export function filterEmployeeVisibleCustomFields(
  fields: CustomField[] | undefined | null,
): CustomField[] {
  return (fields ?? []).filter(isEmployeeVisibleCustomField);
}

/**
 * Whether an EMPLOYEE may EDIT a custom field in the app (#10b "editable by" axis). A field is
 * editable only when it is employee-visible AND flagged `editableByEmployee`. When false the field
 * must still render (see {@link isEmployeeVisibleCustomField}) but read-only: the api rejects writes
 * to it, and the app disables its input and skips it on submit so no request is sent.
 *
 * `editableByEmployee` is optional in the generated client until `generate:api` is re-run against
 * the updated api; treat a missing flag as editable (the permissive default) so existing fields
 * keep working.
 */
export function isEmployeeEditableCustomField(field: CustomField): boolean {
  if (!isEmployeeVisibleCustomField(field)) {
    return false;
  }

  return field.customFieldDefinition.editableByEmployee !== false;
}
